import React, {useEffect, useState} from 'react';
import {FiArrowLeft, FiChevronRight, FiEdit, FiHome, FiKey, FiPause, FiPlay, FiSave} from "react-icons/fi";
import {useTranslation} from "react-i18next";
import {Link, useParams} from "react-router-dom";
import cx from 'classnames';
import moment from "moment";
import 'moment/locale/fr';
import Modal from "../components/Modal";
import {useForm} from "react-hook-form";
import {toast} from "../components/ui";
import {getPartnerById, resetPartnerAdminPassword, updatePartner, updatePartnerStatus} from "../servicesV2/partnerService";

const STATUS_BADGES = {
  active: {label: 'Actif', className: 'bg-green-100 text-green-700'},
  suspended: {label: 'Suspendu', className: 'bg-gray-100 text-gray-600'},
  inactive: {label: 'Inactif', className: 'bg-red-100 text-red-700'},
};

const REQUEST_STATUS_BADGES = {
  pending: {label: 'En attente', className: 'bg-yellow-100 text-yellow-700'},
  approved: {label: 'Approuvé', className: 'bg-blue-100 text-blue-700'},
  provisioning: {label: 'Provisionnement', className: 'bg-blue-100 text-blue-700'},
  active: {label: 'Actif', className: 'bg-green-100 text-green-700'},
  rejected: {label: 'Rejeté', className: 'bg-red-100 text-red-700'},
};

const TABS = ['clients', 'requests', 'config'];

function EditPartnerModal({close, partner, fetchPartner}) {
  const {t} = useTranslation();
  const {register, handleSubmit, formState: {errors}} = useForm({
    defaultValues: {
      company_name: partner.company_name || '',
      contact_email: partner.contact_email || '',
      contact_phone: partner.contact_phone || '',
      max_clients: partner.max_clients || 50,
    }
  });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await updatePartner({
        company_name: data.company_name,
        contact_email: data.contact_email || undefined,
        contact_phone: data.contact_phone || undefined,
        max_clients: data.max_clients ? parseInt(data.max_clients) : 50,
      }, partner.id);
      fetchPartner();
      close();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <div className="flex flex-col gap-4">
        <div>
          <label className="text-xs text-gray-500 font-semibold">{t("PARTNERS.FORM.COMPANY_NAME")} *</label>
          <input
            {...register("company_name", {required: t("GLOBAL.REQUIRED_ERROR")})}
            className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1"
          />
          {errors.company_name && <p className="text-xs text-red-700 font-semibold mt-1">{errors.company_name.message}</p>}
        </div>
        <div>
          <label className="text-xs text-gray-500 font-semibold">{t("PARTNERS.FORM.CONTACT_EMAIL")}</label>
          <input {...register("contact_email")} type="email" className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1"/>
        </div>
        <div>
          <label className="text-xs text-gray-500 font-semibold">{t("PARTNERS.FORM.CONTACT_PHONE")}</label>
          <input {...register("contact_phone")} className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1"/>
        </div>
        <div>
          <label className="text-xs text-gray-500 font-semibold">{t("PARTNERS.FORM.MAX_CLIENTS")}</label>
          <input {...register("max_clients")} type="number" min="1" className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1"/>
        </div>
      </div>
      <div className="flex items-center justify-end gap-3 mt-6">
        <button type="button" onClick={close}
                className="hover:text-azure hover:border-azure flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-semibold text-gray-600">
          {t("GLOBAL.CANCEL")}
        </button>
        <button type="submit" disabled={loading}
                className="flex items-center gap-2 px-4 py-1.5 rounded-lg border border-azure bg-azure text-xs font-semibold text-white hover:shadow-lg disabled:opacity-50">
          {loading ? <div className="loader" style={{width: 16, height: 16}}/> : t("GLOBAL.SAVE")}
        </button>
      </div>
    </form>
  );
}

function ResetAdminPasswordModal({close, partner}) {
  const {t} = useTranslation();
  const {register, handleSubmit, watch, formState: {errors}} = useForm();
  const [loading, setLoading] = useState(false);
  const newPassword = watch("password");

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      await resetPartnerAdminPassword(partner.id, data.password);
      toast.success(
        t("PARTNERS.RESET_PASSWORD.SUCCESS", {defaultValue: "Mot de passe réinitialisé"}),
        {description: partner.company_name},
      );
      close();
    } catch (err) {
      console.error(err);
      const serverKey = err?.response?.data?.message;
      toast.error(
        t("PARTNERS.RESET_PASSWORD.ERROR", {defaultValue: "Échec de la réinitialisation"}),
        {description: serverKey || err?.message},
      );
    } finally {
      setLoading(false);
    }
  };

  const req = t("GLOBAL.REQUIRED_ERROR", {defaultValue: "Champ requis"});

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <p className="text-sm text-gray-500 mb-4">
        {t("PARTNERS.RESET_PASSWORD.HINT", {
          defaultValue: "Définir un nouveau mot de passe pour le compte administrateur du partenaire.",
        })}
      </p>
      <div className="flex flex-col gap-4">
        <div>
          <label className="text-xs text-gray-500 font-semibold">
            {t("PARTNERS.RESET_PASSWORD.NEW_PASSWORD", {defaultValue: "Nouveau mot de passe"})} *
          </label>
          <input
            {...register("password", {
              required: req,
              minLength: {
                value: 8,
                message: t("PARTNERS.RESET_PASSWORD.MIN_LENGTH", {
                  defaultValue: "8 caractères minimum",
                }),
              },
            })}
            type="password"
            autoFocus
            className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1"
          />
          {errors.password && <p className="text-xs text-red-700 font-semibold mt-1">{errors.password.message}</p>}
        </div>
        <div>
          <label className="text-xs text-gray-500 font-semibold">
            {t("PARTNERS.RESET_PASSWORD.CONFIRM_PASSWORD", {defaultValue: "Confirmer le mot de passe"})} *
          </label>
          <input
            {...register("confirmPassword", {
              required: req,
              validate: (v) =>
                v === newPassword ||
                t("PARTNERS.RESET_PASSWORD.MISMATCH", {defaultValue: "Les mots de passe ne correspondent pas"}),
            })}
            type="password"
            className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1"
          />
          {errors.confirmPassword && <p className="text-xs text-red-700 font-semibold mt-1">{errors.confirmPassword.message}</p>}
        </div>
      </div>
      <div className="flex items-center justify-end gap-3 mt-6">
        <button type="button" onClick={close}
                className="hover:text-azure hover:border-azure flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-semibold text-gray-600">
          {t("GLOBAL.CANCEL")}
        </button>
        <button type="submit" disabled={loading}
                className="flex items-center gap-2 px-4 py-1.5 rounded-lg border border-azure bg-azure text-xs font-semibold text-white hover:shadow-lg disabled:opacity-50">
          {loading ? <div className="loader" style={{width: 16, height: 16}}/> : t("PARTNERS.RESET_PASSWORD.SUBMIT", {defaultValue: "Réinitialiser"})}
        </button>
      </div>
    </form>
  );
}

function PartnerDetail() {
  const {t} = useTranslation();
  const {partnerId} = useParams();

  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('clients');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const openEditModal = () => {
    document.getElementsByTagName('body')[0].style.overflow = 'hidden';
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    document.getElementsByTagName('body')[0].style.overflow = 'auto';
    setIsEditModalOpen(false);
  };

  const openResetPasswordModal = () => {
    document.getElementsByTagName('body')[0].style.overflow = 'hidden';
    setIsResetPasswordModalOpen(true);
  };

  const closeResetPasswordModal = () => {
    document.getElementsByTagName('body')[0].style.overflow = 'auto';
    setIsResetPasswordModalOpen(false);
  };

  const fetchPartner = async () => {
    try {
      const res = await getPartnerById(partnerId);
      setPartner(res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async () => {
    if (!partner) return;
    const newStatus = partner.status === 'active' ? 'suspended' : 'active';
    setStatusLoading(true);
    try {
      await updatePartnerStatus(partner.id, newStatus);
      fetchPartner();
    } catch (err) {
      console.error(err);
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    fetchPartner();
  }, [partnerId]);

  if (loading) {
    return (
      <div>
        <div className="text-gray-500 flex items-center gap-2 mt-2 mb-6">
          <span className="text-xs font-semibold flex items-center">
            <FiHome size={12}/> <FiChevronRight size={12}/>
          </span>
          <Link to="/partners" className="text-xs font-semibold text-gray-500 hover:text-azure">{t("ROOT.NAV.PARTNERS")}</Link>
          <FiChevronRight size={12} className="text-gray-400"/>
          <h4 className="text-xs font-semibold text-gray-500">{t("PARTNERS.DETAIL.TITLE")}</h4>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="loader"/>
        </div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div>
        <div className="text-gray-500 flex items-center gap-2 mt-2 mb-6">
          <span className="text-xs font-semibold flex items-center">
            <FiHome size={12}/> <FiChevronRight size={12}/>
          </span>
          <Link to="/partners" className="text-xs font-semibold text-gray-500 hover:text-azure">{t("ROOT.NAV.PARTNERS")}</Link>
        </div>
        <p className="text-sm text-red-500 text-center mt-10">{t("PARTNERS.DETAIL.NOT_FOUND")}</p>
      </div>
    );
  }

  const badge = STATUS_BADGES[partner.status] || STATUS_BADGES.inactive;
  const clients = partner.clients || [];
  const requests = partner.client_requests || [];
  const config = partner.config || {};

  return (
    <div>
      {/* Breadcrumb */}
      <div className="text-gray-500 flex items-center gap-2 mt-2 mb-6">
        <span className="text-xs font-semibold flex items-center">
          <FiHome size={12}/> <FiChevronRight size={12}/>
        </span>
        <Link to="/partners" className="text-xs font-semibold text-gray-500 hover:text-azure">{t("ROOT.NAV.PARTNERS")}</Link>
        <FiChevronRight size={12} className="text-gray-400"/>
        <h4 className="text-xs font-semibold text-gray-500">{partner.company_name}</h4>
      </div>

      {/* Back link */}
      <Link to="/partners" className="flex items-center gap-1 text-sm text-azure hover:underline mb-4">
        <FiArrowLeft size={14}/>
        {t("GLOBAL.BACK")}
      </Link>

      {/* Info Card */}
      <div className="bg-white rounded-lg customShadow p-5 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold mb-1">{partner.company_name}</h2>
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
              {partner.contact_email && <span>{partner.contact_email}</span>}
              {partner.contact_phone && <span>{partner.contact_phone}</span>}
            </div>
            <span className={cx('px-2 py-0.5 rounded-full text-xs font-semibold', badge.className)}>
              {badge.label}
            </span>
            <span className="ml-3 text-xs text-gray-400">
              {t("PARTNERS.TABLE.MAX_CLIENTS")}: {partner.max_clients ?? 50}
            </span>
            <span className="ml-3 text-xs text-gray-400">
              {t("GLOBAL.CREATED_AT")}: {moment(partner.created_at).format('DD/MM/YYYY')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openEditModal}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-semibold text-gray-600 hover:text-azure hover:border-azure"
            >
              <FiEdit size={14}/>
              {t("GLOBAL.EDIT")}
            </button>
            <button
              onClick={openResetPasswordModal}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-semibold text-gray-600 hover:text-azure hover:border-azure"
            >
              <FiKey size={14}/>
              {t("PARTNERS.ACTIONS.RESET_PASSWORD", {defaultValue: "Réinitialiser le mot de passe"})}
            </button>
            <button
              onClick={handleStatusToggle}
              disabled={statusLoading}
              className={cx('flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-semibold disabled:opacity-50', {
                'text-red-600 hover:border-red-600': partner.status === 'active',
                'text-green-600 hover:border-green-600': partner.status !== 'active',
              })}
            >
              {partner.status === 'active' ? <><FiPause size={14}/> {t("PARTNERS.ACTIONS.SUSPEND")}</> : <><FiPlay size={14}/> {t("PARTNERS.ACTIONS.ACTIVATE")}</>}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0 mb-4 border-b">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cx('px-4 py-2 text-sm font-semibold border-b-2 -mb-px', {
              'border-azure text-azure': activeTab === tab,
              'border-transparent text-gray-500 hover:text-gray-700': activeTab !== tab,
            })}
          >
            {t(`PARTNERS.TABS.${tab.toUpperCase()}`)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'clients' && (
        <div className="bg-white p-0 rounded-lg customShadow">
          <table className="min-w-full border-collapse">
            <thead className="bg-gray-100 customShadow">
            <tr>
              <th className="text-left py-3 px-3 uppercase font-semibold text-xs">{t("CLIENTS.TABLE.NOM")}</th>
              <th className="text-left py-3 px-3 uppercase font-semibold text-xs">{t("CLIENTS.TABLE.KEY")}</th>
              <th className="text-left py-3 px-3 uppercase font-semibold text-xs">{t("PARTNERS.TABLE.STATUS")}</th>
              <th className="text-left py-3 px-3 uppercase font-semibold text-xs">{t("GLOBAL.CREATED_AT")}</th>
            </tr>
            </thead>
            <tbody className="text-gray-800 text-sm">
            {clients.length > 0 ? clients.map((client) => (
              <tr key={client.id} className="border">
                <td className="text-left py-3 px-3 font-medium">{client.company_name || client.Nom}</td>
                <td className="text-left py-3 px-3">{client.Key || client.tenant_db_name}</td>
                <td className="text-left py-3 px-3">
                  <span className={cx('px-2 py-0.5 rounded-full text-xs font-semibold', (STATUS_BADGES[client.status] || STATUS_BADGES.active).className)}>
                    {(STATUS_BADGES[client.status] || STATUS_BADGES.active).label}
                  </span>
                </td>
                <td className="text-left py-3 px-3">{moment(client.created_at).format('DD/MM/YYYY')}</td>
              </tr>
            )) : (
              <tr><td colSpan={4} className="text-center py-8 text-gray-400 text-sm">{t("PARTNERS.DETAIL.NO_CLIENTS")}</td></tr>
            )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'requests' && (
        <div className="bg-white p-0 rounded-lg customShadow">
          <table className="min-w-full border-collapse">
            <thead className="bg-gray-100 customShadow">
            <tr>
              <th className="text-left py-3 px-3 uppercase font-semibold text-xs">{t("CLIENT_REQUESTS.TABLE.COMPANY_NAME")}</th>
              <th className="text-left py-3 px-3 uppercase font-semibold text-xs">{t("CLIENT_REQUESTS.TABLE.MODULES")}</th>
              <th className="text-left py-3 px-3 uppercase font-semibold text-xs">{t("CLIENT_REQUESTS.TABLE.STATUS")}</th>
              <th className="text-left py-3 px-3 uppercase font-semibold text-xs">{t("CLIENT_REQUESTS.TABLE.SUBMITTED_AT")}</th>
            </tr>
            </thead>
            <tbody className="text-gray-800 text-sm">
            {requests.length > 0 ? requests.map((req) => {
              const reqBadge = REQUEST_STATUS_BADGES[req.status] || REQUEST_STATUS_BADGES.pending;
              return (
                <tr key={req.id} className="border">
                  <td className="text-left py-3 px-3 font-medium">{req.company_name}</td>
                  <td className="text-left py-3 px-3 text-xs">
                    {Array.isArray(req.requested_modules) ? req.requested_modules.join(', ') : (req.requested_modules || '-')}
                  </td>
                  <td className="text-left py-3 px-3">
                    <span className={cx('px-2 py-0.5 rounded-full text-xs font-semibold', reqBadge.className)}>
                      {reqBadge.label}
                    </span>
                  </td>
                  <td className="text-left py-3 px-3">{moment(req.created_at).format('DD/MM/YYYY')}</td>
                </tr>
              );
            }) : (
              <tr><td colSpan={4} className="text-center py-8 text-gray-400 text-sm">{t("PARTNERS.DETAIL.NO_REQUESTS")}</td></tr>
            )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'config' && (
        <div className="bg-white rounded-lg customShadow p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("PARTNERS.CONFIG.ALLOWED_MODULES")}</h3>
              {config.allowed_modules && Array.isArray(config.allowed_modules) ? (
                <div className="flex flex-wrap gap-2">
                  {config.allowed_modules.map((mod) => (
                    <span key={mod} className="bg-azure/10 text-azure text-xs font-semibold px-2 py-1 rounded-full">{mod}</span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">{t("PARTNERS.CONFIG.ALL_MODULES")}</p>
              )}
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("PARTNERS.CONFIG.CUSTOM_DOMAIN")}</h3>
              <p className="text-sm text-gray-600">{config.custom_domain || '-'}</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("PARTNERS.CONFIG.BRANDING")}</h3>
              {config.branding ? (
                <div className="flex items-center gap-4">
                  {config.branding.logo && <img src={config.branding.logo} alt="Logo" className="h-10 w-10 object-contain rounded"/>}
                  {config.branding.primary_color && (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full border" style={{backgroundColor: config.branding.primary_color}}/>
                      <span className="text-xs text-gray-500">{config.branding.primary_color}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400">{t("PARTNERS.CONFIG.NO_BRANDING")}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} close={closeEditModal} title={t("PARTNERS.EDIT_MODAL_TITLE")}>
        <EditPartnerModal close={closeEditModal} partner={partner} fetchPartner={fetchPartner}/>
      </Modal>

      {/* Reset Admin Password Modal */}
      <Modal
        isOpen={isResetPasswordModalOpen}
        close={closeResetPasswordModal}
        title={t("PARTNERS.RESET_PASSWORD.MODAL_TITLE", {defaultValue: "Réinitialiser le mot de passe administrateur"})}
      >
        <ResetAdminPasswordModal close={closeResetPasswordModal} partner={partner}/>
      </Modal>
    </div>
  );
}

export default PartnerDetail;
